import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { ReverseLeftIcon, XIcon } from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import ISFPUploadForm from '@/features/isfp-upload/components/ISFPUploadForm'
import CostAdjustmentContent from '@/features/isfp-upload/components/OptionalFields/CostAdjustmentContent'
import FundingContent from '@/features/isfp-upload/components/OptionalFields/FundingContent'
import { useISFPFormController } from '@/features/isfp-upload/hooks/useISFPFormController'
import { initializeRetrofits } from '@/features/isfp-upload/utils/initializeAssessmentData'
import { mapExtractedDataToForm } from '@/features/isfp-upload/utils/mapExtractedDataToForm'
import {
  AssessmentDataValidationError,
  validateAssessmentData as validateAssessmentDataUtil,
} from '@/features/isfp-upload/utils/validateAssessmentData'
import { useCustomAssessmentInput, useISFPData, useSubmitCombinedData } from '@/shared/api'
import Button from '@/shared/components/Button'
import LoadingIndicator from '@/shared/components/LoadingIndicator'
import TextButton from '@/shared/components/TextButton'
import type { CustomAssessmentInput } from '@/shared/types/custom-assessment-input.types'
import type { ISFPExtractedData } from '@/shared/types/isfp.types'

import type { EditISFPDataPopupProps } from './EditISFPDataPopup.types'
import EditISFPTabToggle from './EditISFPTabToggle'
import { EditISFPTab } from './EditISFPTabToggle.types'

const EditISFPDataPopup = ({ open, onClose }: EditISFPDataPopupProps) => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const [selectedTab, setSelectedTab] = useState<EditISFPTab>(EditISFPTab.ISFPData)
  const [assessmentData, setAssessmentData] = useState<CustomAssessmentInput | null>(null)
  const [showHeaderShadow, setShowHeaderShadow] = useState(false)
  const [showFooterShadow, setShowFooterShadow] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Initialize form controller
  const formController = useISFPFormController()

  // Fetch ISFP data and custom assessment input when popup opens
  const {
    data: isfpData,
    isLoading: isLoadingISFP,
    error: isfpError,
  } = useISFPData(buildingUuid, {
    enabled: open && !!buildingUuid,
  })

  const {
    data: customAssessmentInput,
    isLoading: isLoadingCustomAssessment,
    error: customAssessmentError,
  } = useCustomAssessmentInput(buildingUuid, {
    enabled: open && !!buildingUuid,
  })

  const isLoading = isLoadingISFP || isLoadingCustomAssessment
  const hasError = isfpError || customAssessmentError

  // Populate form with fetched ISFP data
  useEffect(() => {
    if (!open) {
      formController.reset()
      return
    }

    if (isfpData) {
      const formData = mapExtractedDataToForm(isfpData)
      formController.prefillForm(formData)
      formController.trigger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isfpData, open])

  // Store assessment data when fetched
  useEffect(() => {
    if (!open) {
      setAssessmentData(null)
      return
    }

    if (customAssessmentInput) {
      setAssessmentData({
        buildingId: customAssessmentInput.buildingId,
        subsidyInput: customAssessmentInput.subsidyInput,
        energyPrices: customAssessmentInput.energyPrices,
        retrofits: initializeRetrofits(customAssessmentInput.retrofits),
      })
    }
  }, [customAssessmentInput, open])

  // Reset selected tab when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTab(EditISFPTab.ISFPData)
    }
  }, [open])

  // Handle scroll to show/hide header and footer shadows
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
        setShowHeaderShadow(scrollTop > 0)
        setShowFooterShadow(scrollTop + clientHeight < scrollHeight - 1)
      }
    }

    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      // Check initial state
      handleScroll()
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scrollContainerRef.current, selectedTab])

  // Submit combined data mutation
  const { mutate: submitCombinedData, isPending: isSaving } = useSubmitCombinedData({
    onSuccess: () => {
      toast.success('Daten erfolgreich gespeichert', {
        position: 'top-right',
        autoClose: 3000,
      })
      onClose()
    },
  })

  const handleValidateAssessmentData = (data: CustomAssessmentInput): boolean => {
    // Use shared validation utility
    const validationError = validateAssessmentDataUtil(data)

    if (validationError !== AssessmentDataValidationError.NONE) {
      // Switch to the costs and prices tab to show errors
      setSelectedTab(EditISFPTab.CostsAndPrices)

      // Show appropriate error message based on error type
      if (validationError === AssessmentDataValidationError.ENERGY_PRICE_ERROR) {
        toast.error('Bitte füllen Sie alle Energiepreise aus', {
          position: 'top-right',
          autoClose: 3000,
        })
      } else if (validationError === AssessmentDataValidationError.RETROFIT_ERROR) {
        toast.error('Bitte füllen Sie alle Sanierungsdaten aus', {
          position: 'top-right',
          autoClose: 3000,
        })
      }

      return false
    }

    return true
  }

  const handleSave = async () => {
    let formData: ISFPExtractedData | undefined

    await formController.handleSubmit(
      (fd: ISFPExtractedData) => {
        formData = fd
      },
      () => {
        // onError callback - called when validation fails
        setSelectedTab(EditISFPTab.ISFPData)
        // Wait for next frame to ensure tab content is rendered before scrolling
        setTimeout(() => {
          formController.scrollToFirstError()
        }, 0)
      }
    )()

    if (!formData || !assessmentData || !buildingUuid) return

    // Validate assessmentData
    if (!handleValidateAssessmentData(assessmentData)) return

    submitCombinedData({
      isfpExtractedData: formData,
      customAssessmentInput: assessmentData,
      buildingId: buildingUuid,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#ffffff',
            borderRadius: '18px',
            border: '1px solid #E1E1E4',
            boxShadow: 'none',
            margin: '24px',
            maxWidth: '880px',
          },
        },
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingY: 4,
          }}
        >
          <LoadingIndicator size={60} />
        </Box>
      )}

      {!isLoading && hasError && (
        <Box sx={{ padding: 3 }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '16px',
              color: '#E53935',
              textAlign: 'center',
              paddingY: 4,
            }}
          >
            Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.
          </Typography>
        </Box>
      )}

      {!isLoading && !hasError && (
        <>
          {/* Sticky Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
              padding: '24px',
              boxShadow: showHeaderShadow ? '0px 4px 16px rgba(0, 0, 0, 0.15)' : 'none',
              transition: 'box-shadow 0.2s ease',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                }}
              >
                ISFP-Daten bearbeiten
              </Typography>
              <IconButton onClick={onClose} sx={{ padding: '12px', margin: '-12px' }}>
                <XIcon style={{ width: 24, height: 24, color: '#181D29' }} />
              </IconButton>
            </Box>

            <Box sx={{ margin: '-3px' }}>
              <EditISFPTabToggle selectedTab={selectedTab} onTabChange={setSelectedTab} />
            </Box>
          </Box>

          {/* Scrollable Content */}
          <Box
            ref={scrollContainerRef}
            sx={{
              overflowY: 'auto',
              padding: '0 24px',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {selectedTab === EditISFPTab.ISFPData && <ISFPUploadForm controller={formController} />}

            {selectedTab === EditISFPTab.CostsAndPrices && (
              <CostAdjustmentContent
                formController={formController}
                assessmentData={assessmentData}
                setAssessmentData={setAssessmentData}
              />
            )}

            {selectedTab === EditISFPTab.Funding && (
              <FundingContent
                assessmentData={assessmentData}
                setAssessmentData={setAssessmentData}
              />
            )}
          </Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 1,
              padding: '16px',
              display: 'flex',
              gap: '8px',
              boxShadow: showFooterShadow ? '0px -4px 16px rgba(0, 0, 0, 0.15)' : 'none',
              transition: 'box-shadow 0.2s ease',
            }}
          >
            <TextButton
              onClick={onClose}
              height="56px"
              disabled={isSaving}
              sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ReverseLeftIcon width={24} height={24} />
              Alle Daten zurücksetzen
            </TextButton>
            <Button onClick={handleSave} disabled={isSaving} height="56px" sx={{ flex: 1 }}>
              Speichern
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  )
}

export default EditISFPDataPopup
